import { getDB } from "./schema.js";
import { nowIso, todayDate } from "../utils/dates.js";
import { getPerson, personRoute } from "../api/people.js";
import { addNotification } from "./notifications.js";
import { loadPersonFacets } from "./people.js";
import { followsChanged } from "./cloud-follows.js";

// "New from …" (design_documents/people.md, decision 7): once a week each
// followed person's works list is compared with what we saw last time; a new
// entry — an announced game, a new film or series, a new book — rings the bell.
// The first look at someone is silent (it just records what's there). Comic
// creators (Metron) are left to the comics features.

const STORE = "followed_people";
const WEEK_DAYS = 7;
const PER_PASS = 20;
const SEEN_CAP = 500;
const LAST_RUN_KEY = "peopleWatchLastRun";

const mediaParam = (p) => (p.media_type === "videogame" ? "game" : p.media_type === "anime" ? "anime" : null);

export function checkDue(person, today) {
	if (!person.last_works_check) return true;
	const days = (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${person.last_works_check}T00:00:00Z`)) / 86400000;
	return days >= WEEK_DAYS;
}

// Pure: what's new for a person given a fresh works list. → { next, fresh }.
export function diffWorks(person, works, today, { silent = false } = {}) {
	const seen = new Set(person.seen_work_ids ?? []);
	const fresh = [];
	for (const w of works ?? []) {
		const id = String(w.external_id);
		if (seen.has(id)) continue;
		seen.add(id);
		if (!silent) fresh.push(w);
	}
	return { next: { ...person, seen_work_ids: [...seen].slice(-SEEN_CAP), last_works_check: today }, fresh };
}

export function newWorkNotices(person, fresh, now = nowIso()) {
	return fresh.map((w) => ({
		id: `person-new:${person.person_key}:${w.external_id}`,
		type: "person-new",
		title: person.name,
		body: `New: ${w.title}${w.year ? ` (${w.year})` : ""}${w.roles?.length ? ` · ${w.roles.slice(0, 2).join(", ").toLowerCase()}` : ""}`,
		route: personRoute(person.source, person.external_id, person.media_type),
		cover_url: w.cover_url ?? person.image ?? null,
		media_type: w.media_type ?? person.media_type ?? null,
		item_id: person.person_key,
		created_at: now,
		read: false,
	}));
}

// Check one person now. Silent when we've never looked before.
export async function checkPerson(person, { today = todayDate() } = {}) {
	const data = await getPerson(person.source, person.external_id, { media: mediaParam(person) });
	const silent = !person.last_works_check && !(person.seen_work_ids?.length);
	const { next, fresh } = diffWorks(person, data.works, today, { silent });
	const db = await getDB();
	await db.put(STORE, next);
	const notices = newWorkNotices(next, fresh);
	for (const n of notices) await addNotification(n);
	return { person: next, notices };
}

// The daily maintenance hook: at most once a day, weekly per person, capped.
export async function runPeopleChecks({ today = todayDate(), force = false } = {}) {
	try {
		if (!force && localStorage.getItem(LAST_RUN_KEY) === today) return null;
	} catch {
		// no storage — run anyway
	}
	if (typeof navigator !== "undefined" && navigator.onLine === false) return null;
	const db = await getDB();
	const due = (await db.getAll(STORE))
		.filter((p) => p.source !== "metron" && (force || checkDue(p, today)))
		.sort((a, b) => (a.last_works_check ?? "").localeCompare(b.last_works_check ?? ""))
		.slice(0, PER_PASS);
	let posted = 0;
	for (const p of due) {
		try {
			posted += (await checkPerson(p, { today })).notices.length;
		} catch (err) {
			console.warn(`"New from" check failed for ${p.name}:`, err?.message ?? err);
		}
	}
	if (due.length) {
		await loadPersonFacets();
		followsChanged();
	}
	try {
		localStorage.setItem(LAST_RUN_KEY, today);
	} catch {
		// fine
	}
	return posted;
}
